function toICSDateStringUTC(isoUtc: string): string {
  const d = new Date(isoUtc);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

function toICSDateStringET(isoUtc: string): string {
  const d = new Date(isoUtc);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${get('year')}${get('month')}${get('day')}T${get('hour')}${get('minute')}${get('second')}`;
}

const ICS_VTIMEZONE = [
  'BEGIN:VTIMEZONE', 'TZID:America/New_York',
  'BEGIN:STANDARD', 'DTSTART:19701101T020000', 'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU',
  'TZOFFSETFROM:-0400', 'TZOFFSETTO:-0500', 'TZNAME:EST', 'END:STANDARD',
  'BEGIN:DAYLIGHT', 'DTSTART:19700308T020000', 'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU',
  'TZOFFSETFROM:-0500', 'TZOFFSETTO:-0400', 'TZNAME:EDT', 'END:DAYLIGHT',
  'END:VTIMEZONE',
];

function escapeICS(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

function buildPropertyLabel(lease: { propertyName?: string | null; unitNumber?: string | null; propertyUnitCount?: number } | null | undefined): string {
  if (!lease?.propertyName) return '';
  if (lease.propertyUnitCount && lease.propertyUnitCount > 1 && lease.unitNumber) {
    return `#${lease.unitNumber} - ${lease.propertyName}`;
  }
  return lease.propertyName;
}

export function downloadMoveInICS(
  startTime: string,
  endTime: string,
  lease?: { propertyName?: string | null; unitNumber?: string | null; propertyUnitCount?: number; address?: string | null } | null,
): void {
  const now = new Date();
  const uid = `movein-${now.getTime()}@amberpeak.ca`;
  const label = buildPropertyLabel(lease);
  const summary = label ? `Move-In Appointment — ${label}` : 'Move-In Appointment — Amber Peak';

  const descParts = [
    'WHAT IS INCLUDED',
    '• Key handover and walkthrough of your new unit with an Amber Peak representative',
    '• Documentation of the unit condition at move-in',
    '• Overview of building amenities, garbage disposal, and house rules',
    '',
    'WHAT TO BRING',
    '• Valid photo ID',
    '• Proof of tenant insurance',
    '• Any outstanding documents',
    '',
    'NEED TO RESCHEDULE?',
    'Visit the Amber Peak Tenant Portal to reschedule your appointment:',
    'https://app.amberpeak.ca',
  ];

  if (lease?.address) descParts.splice(0, 0, `LOCATION: ${lease.address}`, '');

  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Amber Peak//Tenant Portal//EN',
    'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', ...ICS_VTIMEZONE,
    'BEGIN:VEVENT',
    `DTSTART;TZID=America/New_York:${toICSDateStringET(startTime)}`,
    `DTEND;TZID=America/New_York:${toICSDateStringET(endTime)}`,
    `DTSTAMP:${toICSDateStringUTC(now.toISOString())}`,
    `UID:${uid}`, `SUMMARY:${escapeICS(summary)}`, `DESCRIPTION:${escapeICS(descParts.join('\n'))}`,
    ...(lease?.address ? [`LOCATION:${escapeICS(lease.address)}`] : []),
    'STATUS:CONFIRMED', 'URL:https://app.amberpeak.ca', 'END:VEVENT', 'END:VCALENDAR',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const apptDate = new Date(startTime).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const fileLabel = label ? label.replace(/[^a-zA-Z0-9 #-]/g, '').replace(/\s+/g, '-') : 'Amber-Peak';
  a.download = `Move-In-Appointment-${fileLabel}-${apptDate}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
